import { useCallback } from 'react'
import { useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'

import ROUTES from '~/shared/constants/routes.const'

import { AppLanguage } from '~/types/locale'
import { ButtonPrimary } from '~/components/Buttons'
import { MathVaultProductAPIStat } from '~/redux/apis/user-stats-api'
import { getVideoProgressData } from '~/statistics/utils/get-video-progress-data.utils'
import statisticsAnalytics from '~/analytics/statistics/statisticsAnalytics'

import PieProgress from '../PieProgress'

import MathVaultActivitySummary from './MathVaultActivitySummary'
import MathVaultLevels from './MathVaultLevels'

interface Product {
  product: MathVaultProductAPIStat
}

export default function MathVaultDetails({ product }: Product): React.ReactElement {
  const { t } = useTranslation('statistics')
  const router = useRouter()
  const isFamilyView = router.asPath.includes(ROUTES.FAMILY_ADMIN_PANEL)
  const { summary, nextLevel, nextQuestion, levels, roomColor, supportsCurrentLocale, chestPath } = product

  const getActivityPathUrl = useCallback(
    (levelNo = 1, questionNo = 1) => `${ROUTES.MATH_VAULT}/${chestPath}/${levelNo ?? 1}/${questionNo ?? 1}`,
    [chestPath],
  )
  const locale = supportsCurrentLocale ? undefined : AppLanguage.EN
  const productHref = getActivityPathUrl(nextLevel, nextQuestion)
  const btnText = t('play')

  const onClickContinueBtn = (): void => {
    statisticsAnalytics.logButtonClick(btnText)
  }

  const progressBarData = getVideoProgressData(summary.completedLevelsCount, summary.totalLevelsCount)

  return (
    <div>
      <div className="flex space-x-4 my-4 pt-2">
        <MathVaultActivitySummary summary={summary} />
        <div className="w-full grow font-semibold rounded overflow-hidden shadow-card space-y-3 py-2">
          <p className="text-center">
            <span className="text-purple">{t('levels')}</span>
          </p>
          <PieProgress
            imgAltText={t('alt_texts.video_camera')}
            imgSrcKey="scoreboard/white-chest.svg"
            progressBarData={progressBarData}
            totalSlices={summary.totalLevelsCount}
          />
          <p className="text-center">
            {summary.completedLevelsCount}/{summary.totalLevelsCount}
          </p>
        </div>
      </div>
      <div className="relative w-full pt-2 ">
        <div>
          {Object.entries(levels).map(([level, levelData]) => (
            <MathVaultLevels
              activityPath={getActivityPathUrl(level)}
              key={level}
              level={levelData}
              locale={locale}
              roomColor={roomColor}
            />
          ))}
        </div>
        {!isFamilyView && (
          <ButtonPrimary
            className="ml-auto mt-4 px-7"
            href={productHref}
            locale={locale}
            noPadding
            onClick={onClickContinueBtn}
            size="medium"
          >
            {btnText}
          </ButtonPrimary>
        )}
      </div>
    </div>
  )
}
